import React, { useEffect, useState, useRef } from 'react';
import { path } from 'ramda';
import PropTypes from 'prop-types';
import { HighchartsTidesGraph } from '../common';
import { FormattedDataPoint } from '../device';
import classNames from 'classnames';
import Loader from '../../components/Loader';
import Location from '../../components/Location';
import astro from '../../common/lib/astro';
import {
  getAstroValue,
  timeFormatForUser,
  widgetExpanded,
  handleWidgetChange,
  deviceIsMine
} from '../../common/ambient';
import './TidesWidget.less';

// Transform API response to graph data format
function transformTidesData(apiResponse) {
  if (!apiResponse || !apiResponse.tides) return [];

  // Find the first primary station and get its name
  const primaryStationForecast = apiResponse.tides
    .flatMap(day => day.Forecasts)
    .find(forecast => forecast.Station?.Primary);

  if (!primaryStationForecast) return [];

  const primaryStationName = primaryStationForecast.Station?.Name;

  // Get tides from ONLY this primary station (by matching station name)
  const allTides = apiResponse.tides
    .flatMap(day =>
      day.Forecasts
        .filter(forecast => forecast.Station?.Name === primaryStationName)
        .flatMap(forecast => forecast.Tides || [])
    );

  // Get past tide for interpolation and up to 5 future tides for display
  const now = Date.now() / 1000;

  // Sort all tides by time
  const sortedTides = allTides.sort((a, b) => a.EpochTime - b.EpochTime);

  // Get past tides and future tides
  const pastTides = sortedTides.filter(tide => tide.EpochTime <= now);
  const futureTides = sortedTides.filter(tide => tide.EpochTime > now);

  // Get the most recent past tide (for interpolation) and the next 5 future tides
  const lastPastTide = pastTides.length > 0 ? pastTides[pastTides.length - 1] : null;
  const nextFutureTides = futureTides.slice(0, 5);

  // Build result array
  const result = [];

  // Add interpolation data (previous tide) if available
  if (lastPastTide && nextFutureTides.length > 0) {
    result.push({
      label: '',
      value: lastPastTide.Height?.Imperial?.Value || 0,
      time: lastPastTide.EpochTime,
      type: lastPastTide.Type,
      isInterpolationPoint: true // Mark this for special handling
    });
  }

  // Add future tides to graph format
  nextFutureTides.forEach(tide => {
    const date = new Date(tide.EpochTime * 1000);
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const ampm = hours >= 12 ? 'pm' : 'am';
    const displayHours = hours % 12 || 12;
    const displayMinutes = minutes.toString().padStart(2, '0');

    result.push({
      label: `${displayHours}:${displayMinutes}${ampm}`,
      value: tide.Height?.Imperial?.Value || 0,
      time: tide.EpochTime,
      type: tide.Type
    });
  });

  return result;
}

export default function TidesWidget({ currentDevice, user, device, actions, userActions }) {
  const customLocation = path(['settings', 'tides', 'customLocation'], currentDevice);
  const deviceCoords = path(['info', 'coords', 'coords'], currentDevice);
  const coords = customLocation?.coords || null;
  const locationName = customLocation?.address || null;

  const [tidesData, setTidesData] = useState([]);
  const [responseData, setResponseData] = useState(null);
  const [tideInfo, setTideInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [containerWidth, setContainerWidth] = useState(null);
  const [sunMoon, setSunMoon] = useState(null);
  const [selectingLocation, setSelectingLocation] = useState(false);
  const [pendingLocation, setPendingLocation] = useState(null);
  const containerRef = useRef(null);

  useEffect(() => {
    if (!coords || !coords.lat || coords.lat === 0) {
      return;
    }

    setLoading(true);
    setError(null);

    const { lat, lon } = coords;
    const url = `https://lightning.ambientweather.net/forecast/tides/${lat}/${lon}`;

    fetch(url)
      .then(response => response.json())
      .then(data => {
        const transformedData = transformTidesData(data);
        if (transformedData.length === 0) {
          setError('No upcoming tides');
        } else {
          setTidesData(transformedData);
          setResponseData(data);

          // Calculate current tide info
          const now = Date.now() / 1000;
          const allTides = data.tides
            .flatMap(day =>
              day.Forecasts
                .filter(forecast => forecast.Station?.Primary)
                .flatMap(forecast => forecast.Tides || [])
            )
            .sort((a, b) => a.EpochTime - b.EpochTime);

          const pastTides = allTides.filter(tide => tide.EpochTime <= now);
          const futureTides = allTides.filter(tide => tide.EpochTime > now);

          if (pastTides.length > 0 && futureTides.length > 0) {
            const lastPast = pastTides[pastTides.length - 1];
            const nextFuture = futureTides[0];

            // Interpolate current height
            const timeDiff = nextFuture.EpochTime - lastPast.EpochTime;
            const timeElapsed = now - lastPast.EpochTime;
            const ratio = timeElapsed / timeDiff;
            const heightDiff = (nextFuture.Height?.Imperial?.Value || 0) - (lastPast.Height?.Imperial?.Value || 0);
            const currentHeight = (lastPast.Height?.Imperial?.Value || 0) + (heightDiff * ratio);

            // Determine if rising or falling
            const isRising = nextFuture.Type === 'High';

            // Find next low and high
            const nextHigh = futureTides.find(t => t.Type === 'High');
            const nextLow = futureTides.find(t => t.Type === 'Low');

            const nextHighValue = nextHigh && nextHigh.Height && nextHigh.Height.Imperial
              ? nextHigh.Height.Imperial.Value
              : null;
            const nextLowValue = nextLow && nextLow.Height && nextLow.Height.Imperial
              ? nextLow.Height.Imperial.Value
              : null;

            setTideInfo({
              currentHeight: currentHeight,
              isRising,
              nextHigh: nextHighValue,
              nextLow: nextLowValue
            });
          }
        }
        setLoading(false);
      })
      .catch(err => {
        console.error('Error fetching tides:', err);
        setError('Unable to load tide data');
        setLoading(false);
      });
  }, [coords]);

  useEffect(() => {
    if (!containerRef.current) return;

    const updateWidth = () => {
      if (!containerRef.current) return;
      const width = containerRef.current.offsetWidth;
      if (width > 0) {
        setContainerWidth(width);
      }
    };

    // Initial measurement with slight delay for DOM render
    const timeoutId = setTimeout(updateWidth, 100);

    const resizeObserver = new ResizeObserver(updateWidth);
    resizeObserver.observe(containerRef.current);

    return () => {
      clearTimeout(timeoutId);
      resizeObserver.disconnect();
    };
  }, [tidesData]);

  useEffect(() => {
    if (!coords || !coords.lat || coords.lat === 0) return;

    const rightNow = moment.tz(currentDevice.tz.name);
    const getValue = getAstroValue(moment, currentDevice);

    setSunMoon({
      sun: {
        rise: getValue(astro.solar.rise, rightNow),
        set: getValue(astro.solar.set, rightNow)
      },
      moon: {
        rise: getValue(astro.lunar.rise, rightNow),
        set: getValue(astro.lunar.set, rightNow)
      }
    });
  }, [coords, currentDevice]);

  const handleLocationClick = () => {
    setSelectingLocation(true);
    setPendingLocation(customLocation || null);
  };

  const handleLocationChange = (location) => {
    setPendingLocation(location);
  };

  const handleLocationSave = () => {
    if (pendingLocation && pendingLocation.coords && deviceIsMine(device, currentDevice._id)) {
      handleWidgetChange(userActions, user, null, device, currentDevice, actions,
        { customLocation: pendingLocation }, 'tides');
    }
    setSelectingLocation(false);
    setPendingLocation(null);
  };

  const handleLocationCancel = () => {
    setSelectingLocation(false);
    setPendingLocation(null);
  };

  const handleUseDeviceLocation = () => {
    if (deviceCoords && deviceIsMine(device, currentDevice._id)) {
      handleWidgetChange(userActions, user, null, device, currentDevice, actions,
        { customLocation: { coords: deviceCoords } }, 'tides');
    }
    setSelectingLocation(false);
    setPendingLocation(null);
  };

  const renderSunMoonTimes = () => {
    if (!sunMoon) return null;

    const fmt = timeFormatForUser(user);

    return (
      <div className="sun-moon-times">
        <div className="sun-times">
          <div className="sun-icon" />
          <div className="times">
            <div className="time-row">
              <span className="label">Rise:</span>
              <span className="time">{sunMoon.sun.rise.format(fmt)}</span>
            </div>
            <div className="time-row">
              <span className="label">Set:</span>
              <span className="time">{sunMoon.sun.set.format(fmt)}</span>
            </div>
          </div>
        </div>
        <div className="moon-times">
          <div className="moon-icon" />
          <div className="times">
            <div className="time-row">
              <span className="label">Rise:</span>
              <span className="time">{sunMoon.moon.rise.format(fmt)}</span>
            </div>
            <div className="time-row">
              <span className="label">Set:</span>
              <span className="time">{sunMoon.moon.set.format(fmt)}</span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const isExpanded = widgetExpanded('tides', currentDevice);

  // Show 3 tides when collapsed, 5 when expanded
  const displayTidesData = React.useMemo(() => {
    if (tidesData.length === 0) return tidesData;

    const maxTides = isExpanded ? 5 : 3;
    const hasInterpolationPoint = tidesData[0]?.isInterpolationPoint;

    if (hasInterpolationPoint) {
      // Keep interpolation point + max tides
      return tidesData.slice(0, maxTides + 1);
    } else {
      // Just take max tides
      return tidesData.slice(0, maxTides);
    }
  }, [tidesData, isExpanded]);

  return (
    <div className={classNames('device-tides-widget', {
      tiny: containerWidth < 310,
      'selecting-location': selectingLocation
    })} ref={containerRef}>
      {selectingLocation ? (
        <div className="location-selector">
          <Location
            location={pendingLocation}
            onChange={handleLocationChange}
          />
          <div className="location-buttons">
            <button className="btn btn-secondary" onClick={handleLocationCancel}>
              Cancel
            </button>
            <button
              className="btn btn-primary"
              onClick={handleLocationSave}
              disabled={!pendingLocation || !pendingLocation.coords}
            >
              Save Location
            </button>
          </div>
          {deviceCoords && (
            <div className="clear-custom-location">
              <button className="btn btn-link" onClick={handleUseDeviceLocation}>
                Use Device Location
              </button>
            </div>
          )}
        </div>
      ) : (
        <>
          {!customLocation && (
            <div className="tides-no-location">
              <p>Set a location to see tides.</p>
              <button className="btn btn-primary" onClick={handleLocationClick}>Set Location</button>
            </div>
          )}
          {customLocation && loading && <div className="tides-loading"><Loader /></div>}
          {customLocation && error && (
            <div className="tides-error">
              {error === 'No upcoming tides' ? (
                <>
                  No tides reported for {locationName || responseData?.location?.LocalizedName || 'this location'}.
                  <br />
                  <a onClick={handleLocationClick}>Change location</a>
                </>
              ) : (
                error
              )}
            </div>
          )}
          {customLocation && !loading && !error && tidesData.length > 0 && (
            <div className="tides-graph-container">
              <div className='location' onClick={handleLocationClick}>
                {locationName || responseData?.location?.LocalizedName}
              </div>
              <div className={classNames('tide-details-wrapper', {
                expanded: isExpanded
              })}>
                {tideInfo && (
                  <div className='tide-details'>
                    <div className={`current-height ${tideInfo.isRising ? 'rising' : 'falling'}`}>
                      <FormattedDataPoint type="altitudeft" value={tideInfo.currentHeight} valueTransform={(val) => typeof val === 'number' ? val.toFixed(1) : val} />
                    </div>
                    <div className='next-tides'>
                      {tideInfo.nextLow !== null && <span className='next-tide'>Next Low: <FormattedDataPoint type="altitudeft" value={tideInfo.nextLow} valueTransform={(val) => typeof val === 'number' ? val.toFixed(1) : val} /></span>}
                      {tideInfo.nextHigh !== null && <span className='next-tide'>Next High: <FormattedDataPoint type="altitudeft" value={tideInfo.nextHigh} valueTransform={(val) => typeof val === 'number' ? val.toFixed(1) : val} /></span>}
                    </div>
                  </div>
                )}
                {isExpanded && renderSunMoonTimes()}
              </div>
              <HighchartsTidesGraph data={displayTidesData} height={containerWidth * 0.55} width={containerWidth} user={user} />
              <p>
                Tides forecast by <span className='accuweather-logo' />
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
};

TidesWidget.propTypes = {
  currentDevice: PropTypes.object.isRequired,
  user: PropTypes.object.isRequired,
  device: PropTypes.object.isRequired,
  actions: PropTypes.object.isRequired,
  userActions: PropTypes.object.isRequired
};

TidesWidget.displayName = 'TidesWidget'
